"use client";

// src/context/ProductContext.tsx
import { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode } from 'react';
import { Product } from '../types';

type ProductContextType = {
  allProducts: Product[];
  isLoading: boolean;
  error: string | null;
  loadProducts: () => Promise<void>;
};

const ProductContext = createContext<ProductContextType>({
  allProducts: [],
  isLoading: false,
  error: null,
  loadProducts: async () => {},
});

export const useAllProducts = () => useContext(ProductContext);


const CACHE_KEY = 'astroji_lightweight_catalog_v2';
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds

export const ProductProvider = ({ children }: { children: ReactNode }) => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const inFlightLoadRef = useRef<Promise<void> | null>(null);

  const loadProducts = useCallback(async () => {
    if (hasLoadedRef.current) return;
    if (inFlightLoadRef.current) return inFlightLoadRef.current;

    const fetchAllProducts = async () => {
      setIsLoading(true);
      try {
        // Clear legacy truncated cache if present
        try { localStorage.removeItem('astroji_lightweight_catalog'); } catch {}

        // 1. Check Local Cache
        const cachedStr = localStorage.getItem(CACHE_KEY);
        if (cachedStr) {
          try {
            const { timestamp, products } = JSON.parse(cachedStr);
            if (Date.now() - timestamp < CACHE_TTL && Array.isArray(products) && products.length > 150) {
              setAllProducts(products);
              hasLoadedRef.current = true;
              return; // Cache is still fresh, exit early
            }
          } catch (e) {
            console.warn('Failed to parse catalog cache', e);
          }
        }

        // 2. Fetch Lightweight Data from Server
        const response = await fetch('/api/client-products?mode=all', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Catalog request failed (${response.status})`);

        const payload = await response.json() as { products?: Product[] };
        if (Array.isArray(payload.products)) {
          const finalProducts = payload.products;
          
          setAllProducts(finalProducts);
          hasLoadedRef.current = true;

          // 3. Update Cache Non-blocking
          const cacheData = JSON.stringify({
            timestamp: Date.now(),
            products: finalProducts
          });
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              try { localStorage.setItem(CACHE_KEY, cacheData); } catch {}
            });
          } else {
            setTimeout(() => {
              try { localStorage.setItem(CACHE_KEY, cacheData); } catch {}
            }, 0);
          }
        } else {
          throw new Error('Invalid data structure from API.');
        }
      } catch (err: unknown) {
        setError('Failed to load products. Please try refreshing the page.');
        console.error(err);
      } finally {
        setIsLoading(false);
        inFlightLoadRef.current = null;
      }
    };

    inFlightLoadRef.current = fetchAllProducts();
    return inFlightLoadRef.current;
  }, []);

  const value = useMemo(() => ({
    allProducts, isLoading, error, loadProducts
  }), [allProducts, isLoading, error, loadProducts]);

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};
